import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { queryClient } from "App";
import { Button } from "components/ui/button";
import { sanitizeCSSVarName } from "components/helpers";
import { BaseTableV3Pagination } from "components/BaseTableV3/BaseTableV3Pagination";
import { TableTop } from "components/BaseTableV3/TableTop";
import { ResponsiveTable } from "components/BaseTableV3/ResponsiveTable";
import { isActionsColumn } from "components/BaseTableV3/table-helpers";
import { tableRootKey } from "components/BaseTableV3/queryKeys";
import { useTableConfig } from "hooks/useTableConfig";
import { useColumnSize } from "hooks/useColumnSize";
import useShowDisabledTags from "hooks/useShowDisabledTags";

const defaultData = [];

export const getCommonPinningStyles = (column, isHeader = false) => {
  const isPinned = column.getIsPinned();
  const isSorted = !!column.getIsSorted();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-1px 0 1px -1px gray inset"
      : isFirstRightPinnedColumn
        ? "1px 0 1px -1px gray inset"
        : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: isPinned ? 0.95 : 1,
    position: isPinned ? "sticky" : "relative",
    zIndex: isPinned ? 1 : 0,
    whiteSpace: "nowrap",
    ...(isHeader && isSorted
      ? {
          backgroundColor: "var(--color-sorted-header, #e5e7eb)",
        }
      : {}),
    width: `calc(var(${sanitizeCSSVarName(`--header-${column.id}-size`)}) * 1px)`,
  };
};

// Prepend a View link to the actions cell, reusing the original cell so each
// table's own Edit/Delete dropdown keeps working.
const decorateActionsColumns = (columns, { rowDetailHref, navigate }) => {
  if (!rowDetailHref) return columns;
  const actionsColumn = columns.find(isActionsColumn);
  if (!actionsColumn) return columns;

  const wrapCell = originalCell => ctx => (
    <div
      className="tw:flex tw:items-center tw:gap-1"
      onClick={e => e.stopPropagation()}
    >
      <Button
        variant="ghost"
        size="icon-lg"
        aria-label="View"
        onClick={() => navigate(rowDetailHref(ctx.row.original))}
      >
        <ExternalLink />
      </Button>
      {flexRender(originalCell, ctx)}
    </div>
  );

  return columns.map(col =>
    col.id === actionsColumn.id
      ? { ...col, size: 96, cell: wrapCell(col.cell) }
      : col
  );
};

const BaseTableV3 = ({
  columns = [],
  downloadParams = {},
  downloadConfig = {},
  customFields = [],
  defaultFilters = {},
  extraQueryParams = {},
  fetchData,
  headerIcon,
  header,
  createButton,
  enableSearch,
  queryKey,
  enableSettings = false,
  batchEditButton = null,
  tagsDropdownParams = { visible: false },
  tagsModelType,
  mergeButton = null,
  actionButtons = null,
  tableName,
  filters,
  className,
  exportTableName = "",
  customTableSlug = "",
  rowIdKey = "id",
  invalidateData = null,
  extraButtons = null,
  lastUpdate = null,
  rowDetailHref = null,
  paginationExtra = null,
  refetchInterval = undefined,
}) => {
  const navigate = useNavigate();

  const decoratedColumns = useMemo(
    () => decorateActionsColumns(columns, { rowDetailHref, navigate }),
    [columns, rowDetailHref, navigate]
  );
  const {
    queryParams,
    filterParams,
    tableSettings,
    search,
    sorting,
    pagination,
    columnOrder,
    columnVisibility,
    columnPinning,
    selectedRows,
    tagsFilters,
    customFilters,
    initialized,
    handleUpdateTableSettings,
    handleApplyAdvancedSearch,
    handleResizeColumnQuery,
    handleUncheckAll,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
    setPagination,
    setSelectedRows,
    setSearch,
    setSort,
  } = useTableConfig({
    tableName,
    columns: decoratedColumns,
    filters,
    defaultFilters,
  });

  const showDisabledTags = useShowDisabledTags();
  const requestParams = useMemo(
    () => ({
      ...queryParams,
      show_disabled_tags: showDisabledTags ? "true" : "false",
    }),
    [queryParams, showDisabledTags]
  );

  const rootKey = useMemo(() => tableRootKey(queryKey), [queryKey]);

  const cacheKeys = useMemo(() => {
    return [...rootKey, requestParams, extraQueryParams];
  }, [rootKey, requestParams, extraQueryParams]);

  const {
    data: queryResult,
    isFetched,
    isFetching,
    isError,
  } = useQuery({
    queryKey: cacheKeys,
    queryFn: () => fetchData({ ...requestParams, ...extraQueryParams }),
    initialData: defaultData,
    placeholderData: keepPreviousData,
    enabled: initialized,
    refetchInterval,
  });
  const selectedData = useMemo(() => {
    const cachedQueries = queryClient.getQueriesData({
      queryKey: rootKey,
    });
    const seen = new Set();
    const allCachedRows = cachedQueries
      .flatMap(([, data]) => data?.data ?? [])
      .filter(row => {
        const id = row[rowIdKey];
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    const allSelectedRows = allCachedRows.filter(
      row => selectedRows[row[rowIdKey]] === true
    );
    return {
      data: allSelectedRows,
      ids: allSelectedRows.map(row => row[rowIdKey]),
    };
  }, [rootKey, selectedRows, rowIdKey]);

  const table = useReactTable({
    columnResizeMode: "onChange",
    columnResizeDirection: "ltr",
    data: queryResult?.data ?? defaultData,
    rowCount: queryResult?.total,
    enableColumnResizing: true,
    manualSorting: true,
    // With manual sorting, TanStack infers the first direction from the top row
    // of the current page, which changes after each sort and can skip a direction.
    sortDescFirst: false,
    manualPagination: true,
    manualFiltering: true,
    columns: decoratedColumns,
    defaultColumn: {
      minSize: 60,
      maxSize: 800,
    },
    state: {
      columnVisibility,
      columnPinning,
      pagination,
      columnOrder,
      sorting,
      rowSelection: selectedRows,
    },
    getRowId: row => row[rowIdKey],
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onPaginationChange: setPagination,
    onSortingChange: setSort,
    onRowSelectionChange: setSelectedRows,
    getCoreRowModel: getCoreRowModel(),
  });

  const columnSizeVars = useColumnSize({ table, columns: decoratedColumns });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setColumnOrder(columnOrder => {
          const oldIndex = columnOrder.indexOf(active.id);
          const newIndex = columnOrder.indexOf(over.id);
          const newColumnOrder = arrayMove(columnOrder, oldIndex, newIndex);
          return newColumnOrder;
        });
      }
    },
    [setColumnOrder]
  );

  return (
    <div
      // Google Translate replaces React-owned text nodes, which makes the
      // reconciler throw NotFoundError on removeChild during re-render.
      translate="no"
      className="tw:flex tw:flex-1 tw:flex-col tw:overflow-y-auto"
    >
      <TableTop
        exportTableName={exportTableName}
        customTableSlug={customTableSlug}
        headerIcon={headerIcon}
        header={header}
        createButton={createButton}
        enableSearch={enableSearch}
        searchQuery={search}
        updateSearch={setSearch}
        downloadParams={downloadParams}
        downloadConfig={downloadConfig}
        defaultFilterParams={defaultFilters}
        actionButtons={
          actionButtons
            ? actionButtons({
                selectedIds: selectedData.ids,
                selectedData: selectedData.data,
                uncheckAll: handleUncheckAll,
              })
            : null
        }
        mergeButton={
          mergeButton
            ? mergeButton({
                selectedData: selectedData.data,
                uncheckAll: handleUncheckAll,
              })
            : null
        }
        batchEditButton={
          batchEditButton
            ? batchEditButton({
                selectedIds: selectedData.ids,
                uncheckAll: handleUncheckAll,
                rows: queryResult?.data,
              })
            : null
        }
        enableSettings={enableSettings}
        tableName={tableName}
        columns={decoratedColumns}
        customFields={customFields}
        customFilters={customFilters}
        tableSettings={tableSettings}
        columnOrder={columnOrder}
        handleUpdateTableSettings={handleUpdateTableSettings}
        handleApplyAdvancedSearch={handleApplyAdvancedSearch}
        filters={filters}
        filterParams={filterParams}
        tagsFilters={tagsFilters}
        selectedRowsArr={selectedData.ids}
        rowIdKey={rowIdKey}
        queryParams={queryParams}
        className={className}
        data={queryResult?.data ?? defaultData}
        invalidateData={invalidateData}
        tagsDropdownParams={{
          visible: tagsDropdownParams.visible,
          disabled: tagsDropdownParams.checkForDisabledRows?.(
            selectedData.data
          ),
          disabledTooltip: tagsDropdownParams.disabledTooltip,
        }}
        tagsModelType={tagsModelType}
        extra={queryResult?.extra}
        extraButtons={extraButtons}
        isFetched={isFetched}
        lastUpdate={lastUpdate}
        handleUncheckAll={handleUncheckAll}
      />
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <ResponsiveTable
          table={table}
          columnOrder={columnOrder}
          handleResizeColumnQuery={handleResizeColumnQuery}
          isFetched={isFetched}
          isFetching={isFetching}
          isError={isError}
          queryResult={queryResult}
          columnSizeVars={columnSizeVars}
        />
      </DndContext>
      <BaseTableV3Pagination
        table={table}
        totalIsEstimated={queryResult?.total_is_estimated}
        extra={
          typeof paginationExtra === "function"
            ? paginationExtra(queryResult?.extra, queryResult)
            : paginationExtra
        }
      />
    </div>
  );
};

export default BaseTableV3;
